import {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode,
} from "react";
import { auth, db } from "../api/firebase";
import {
    collection,
    query,
    where,
    onSnapshot,
    Timestamp,
    getDocs,
} from "firebase/firestore";
import { useFirestoreQuery } from "../hooks/useFirestoreQuery";
import { QueryKey } from "../constants/QueryKey";

// Define notification type
export interface Notification {
    id: string;
    title: string;
    content: string;
    type: string;
    priority: string;
    lottieUrl: string;
    startDate: Timestamp;
    endDate: Timestamp;
    createdAt: Timestamp;
    updatedAt: Timestamp;
    createdBy: string;
    urgent: boolean;
}

interface NotificationContextValue {
    notifications: Notification[];
    loading: boolean;
    hasUnreadNotifications: boolean;
    currentNotification: Notification | null;
    markAllAsRead: () => void;
    showNext: () => void;
    dismissCurrent: () => void;
}

const NotificationContext = createContext<NotificationContextValue>({
    notifications: [],
    loading: true,
    hasUnreadNotifications: false,
    currentNotification: null,
    markAllAsRead: () => {},
    showNext: () => {},
    dismissCurrent: () => {},
});

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
    const [activeNotifications, setActiveNotifications] = useState<
        Notification[]
    >([]);
    const [readNotifications, setReadNotifications] = useState<string[]>([]);
    const [currentNotification, setCurrentNotification] =
        useState<Notification | null>(null);

    // Create a query for active notifications
    const now = new Date();
    const notificationsQuery = query(
        collection(db, "notifications"),
        where("startDate", "<=", now),
        where("endDate", ">=", now)
    );

    // Use our custom hook for Firestore queries with React Query
    const { data: notificationsSnapshot, isLoading } = useFirestoreQuery(
        [QueryKey.NOTIFICATIONS],
        notificationsQuery,
        { subscribe: true }
    );

    // Check local storage for read notifications on mount
    useEffect(() => {
        const storedReadNotifications =
            localStorage.getItem("readNotifications");
        if (storedReadNotifications) {
            setReadNotifications(JSON.parse(storedReadNotifications));
        }
    }, []);

    // Save read notifications to local storage when changed
    useEffect(() => {
        localStorage.setItem(
            "readNotifications",
            JSON.stringify(readNotifications)
        );
    }, [readNotifications]);

    // Process the notifications data when the snapshot changes
    useEffect(() => {
        if (!notificationsSnapshot) return;

        const notificationData = notificationsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        })) as Notification[];

        // Filter out permanently dismissed and "don't show again" notifications
        const filteredNotifications = notificationData.filter(
            (notification) =>
                !localStorage.getItem(`do_not_show_${notification.id}`)
        );

        // Sort by priority and creation date
        const sortedNotifications = filteredNotifications.sort((a, b) => {
            // First sort by urgent flag
            if (a.urgent && !b.urgent) return -1;
            if (!a.urgent && b.urgent) return 1;

            // Then by priority
            const priorityOrder = { high: 0, medium: 1, low: 2 };
            const aPriority =
                priorityOrder[a.priority as keyof typeof priorityOrder] || 3;
            const bPriority =
                priorityOrder[b.priority as keyof typeof priorityOrder] || 3;
            if (aPriority !== bPriority) return aPriority - bPriority;

            // Finally by creation date (newest first)
            return b.createdAt.toMillis() - a.createdAt.toMillis();
        });

        setActiveNotifications(sortedNotifications);

        // Reset current notification if it was marked "don't show again"
        if (
            currentNotification &&
            localStorage.getItem(`do_not_show_${currentNotification.id}`)
        ) {
            setCurrentNotification(null);
        }

        // Set the current notification if none is set and there are available notifications
        if (!currentNotification && sortedNotifications.length > 0) {
            const unreadNotifications = sortedNotifications.filter(
                (notification) => !readNotifications.includes(notification.id)
            );
            if (unreadNotifications.length > 0) {
                setCurrentNotification(unreadNotifications[0]);
            }
        }
    }, [notificationsSnapshot, currentNotification, readNotifications]);

    // Calculate if there are unread notifications
    const hasUnreadNotifications = activeNotifications.some(
        (notification) => !readNotifications.includes(notification.id)
    );

    // Mark all notifications as read
    const markAllAsRead = () => {
        // Mark all notifications as both read and "don't show again"
        activeNotifications.forEach((notification) => {
            localStorage.setItem(
                `read_notification_${notification.id}`,
                "true"
            );
            localStorage.setItem(`do_not_show_${notification.id}`, "true");
        });

        const notificationIds = activeNotifications.map(
            (notification) => notification.id
        );
        setReadNotifications((prev) =>
            Array.from(new Set([...prev, ...notificationIds]))
        );
        setCurrentNotification(null);
    };

    // Show next notification
    const showNext = () => {
        if (currentNotification) {
            // Mark current as read
            setReadNotifications((prev) => [...prev, currentNotification.id]);

            // Find next unread notification that hasn't been permanently dismissed or marked "don't show again"
            const unreadNotifications = activeNotifications.filter(
                (notification) =>
                    !readNotifications.includes(notification.id) &&
                    notification.id !== currentNotification.id &&
                    !localStorage.getItem(`do_not_show_${notification.id}`)
            );

            if (unreadNotifications.length > 0) {
                setCurrentNotification(unreadNotifications[0]);
            } else {
                setCurrentNotification(null);
            }
        } else {
            // If no current notification, show the first unread one that hasn't been marked "don't show again"
            const unreadNotifications = activeNotifications.filter(
                (notification) =>
                    !readNotifications.includes(notification.id) &&
                    !localStorage.getItem(`do_not_show_${notification.id}`)
            );

            if (unreadNotifications.length > 0) {
                setCurrentNotification(unreadNotifications[0]);
            }
        }
    };

    // Dismiss current notification
    const dismissCurrent = () => {
        if (currentNotification) {
            setReadNotifications((prev) => [...prev, currentNotification.id]);
            setCurrentNotification(null);
        }
    };

    return (
        <NotificationContext.Provider
            value={{
                notifications: activeNotifications,
                loading: isLoading,
                hasUnreadNotifications,
                currentNotification,
                markAllAsRead,
                showNext,
                dismissCurrent,
            }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => useContext(NotificationContext);
